import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { Platform } from 'react-native';
import { compact, debounce } from 'lodash';

import {
	institutionTypeList,
	statusOptions126,
	reportTypeOptions,
	defaultFilters,
	handleFilter,
} from './RepOrg126Reports.utils';
import {
	getBussinesUnitsModules,
	getUserData,
} from '../../../../store/selectors/login.selectors';
import httpService from '../../../../services/http.service';
import { Privileges } from '../../../../constants/roleTypes.constant';
import useEffectNotInitial from '../../../../hooks/useEffectNotInitial';
import RepOrg126Reports from './RepOrg126Reports.component';
import { FilterData } from '../../../../components/web/Filters/Filters.container';
import { i18nService } from '../../../../services/i18n.service';
import { getSelectedUnits } from '../../rep.utils';
import { useRoute } from '@react-navigation/native';

const RepOrg126ReportsContainer = ({ navigation }: { navigation: any }) => {
	const [requestsData, setRequestsData] = useState([]);
	const [noOperators, setNoOperators] = useState<boolean>(false);
	const [operatorsList, setOperatorsList] = useState<any[]>([]);
	const [isLoading, setIsLoading] = useState<boolean>(false);
	const [businessUnitsList, setBusinessUnitsList] = useState([]);
	const [disabledLoad, setDisableLoad] = useState(false);
	const [nextPage, setNextPage] = useState(0);
	const [order, setOrder] = useState('ASC');
	const [sortBy, setSortBy] = useState('repCustomer');
	const defaultSort = 'repCustomer';
	const defaultOrder = 'ASC';
	const [filters, setFilters] = useState(defaultFilters);
	const [prevReportType, setPrevReportType] = useState(
		defaultFilters.reportType,
	);
	const [repCustData, setRepCustData] = useState(false);

	const userData = useSelector(getUserData);
	const modules = useSelector(getBussinesUnitsModules);
	const route = useRoute();

	const canEnterCustomer = useMemo(
		() =>
			userData?.repRolePrivileges?.includes(
				Privileges.ROLE_REP_ACCESS_CUSTOMER_WRITE,
			),
		[userData?.repRolePrivileges],
	);

	useEffect(() => {
		if (route.params?.filtersData) {
			const f = JSON.parse(route.params?.filtersData);
			f.reportType = [...f.frequency];
			delete f.frequency;
			setFilters(state => ({
				...state,
				...f,
			}));
			navigation.setParams({ filtersData: undefined });
		}
		if (route.params?.customersFullData) {
			const repCustData = JSON.parse(route.params?.customersFullData);
			setRepCustData(
				repCustData.map((d: any) => ({ label: d.name, value: d.id })),
			);
			navigation.setParams({ customersFullData: undefined });
		}
	}, [route.params]);

	const getBusinessUnitsList = async () => {
		const res: any = await httpService.api<string[]>({
			type: 'getRepresntUnits',
			query: { pageName: 'repOrg126Report' },
			disableBI: true,
		});
		setBusinessUnitsList(
			res.map((item: any) => ({
				...item,
				id: item.numericId,
				stringId: item.id,
			})),
		);
		setFilters(state => ({ ...state, unitIds: [res[0]?.numericId] }));
	};

	const getRepCustomerUnitsList = async (
		search: string,
		loadedOptions: null,
		{ page }: { page: number },
	) => {
		const res: any = await httpService.api<string[]>({
			type: 'getRepCustomerUnitsList',
			data: {
				p: page,
				ps: 15,
				search,
				f: {
					unitIds: getSelectedUnits(
						filters.unitIds,
						businessUnitsList,
					),
					repCustomerIds: filters.repCustomerIds,
				},
			},
			query: { pageName: 'repOrg126Report' },
			disableBI: true,
		});
		return {
			options: res?.data?.map((el: any) => {
				return {
					value: el.id,
					label: el.name,
				};
			}),
			hasMore: res?.hasNext,
			additional: {
				page: page + 1,
			},
		};
	};

	const getOperatorList = async () => {
		const res: any = await httpService.api({
			type: 'getCustomerOperatorsList',
			query: {
				repCustomerIds: filters.repCustomerIds.join(','),
				unitIds: getSelectedUnits(
					filters.unitIds,
					businessUnitsList,
				).join(','),
			},
			disableBI: true,
		});
		if (res?.length) {
			setNoOperators(false);
			setOperatorsList([
				{
					id: -1,
					name: i18nService.translate(
						'repCustomersPage.operator.noManager',
					),
				},
				...res,
			]);
		} else {
			setOperatorsList([
				{
					id: -1,
					name: i18nService.translate(
						'repCustomersPage.operator.noManager',
					),
				},
			]);
			setNoOperators(true);
		}
	};

	const onFilter = (data: FilterData) => {
		handleFilter(
			data,
			setFilters,
			filters,
			prevReportType,
			setPrevReportType,
			setRepCustData,
		);
	};

	const has115Module = useMemo(() => {
		if (modules) {
			const module115 = filters?.unitIds.find((unit: any) =>
				modules[unit]?.find(
					(module: any) =>
						module.id === 115 && module?.status === 'ACTIVE',
				),
			);

			if (module115) {
				return true;
			}
			return false;
		}

		return true;
	}, [modules, filters?.unitIds]);

	const repOrgPermission = useMemo(
		() =>
			userData?.repRolePrivileges?.includes(
				Privileges.ROLE_REP_ORG_126_REPORT_WRITE,
			),
		[userData?.repRolePrivileges],
	);

	const disabled = useMemo(
		() => (repOrgPermission && has115Module ? false : true),
		[repOrgPermission, has115Module],
	);

	useEffect(() => {
		setIsLoading(true);
		getBusinessUnitsList();
	}, [userData]);

	useEffectNotInitial(() => {
		if (businessUnitsList.length && filters.unitIds.length) {
			getOperatorList();
			onMount();
		}
	}, [filters.unitIds, filters.repCustomerIds, businessUnitsList]);

	useEffectNotInitial(() => {
		debouncedGetMoreData(true, defaultSort, defaultOrder, filters);
	}, [filters]);

	const onMount = async () => {
		if (Platform.OS !== 'web') {
			const crashlytics = (
				await import('@react-native-firebase/crashlytics')
			).default;
			crashlytics().log('EmplooyeeRequests');
			await crashlytics().setAttributes({
				userId: userData?.id || '',
				userName:
					`${userData?.first_name} ${userData?.last_name}` || '',
			});
		}
	};
	const debouncedGetMoreData = debounce((...args) => {
		getRequestsData(...args);
	}, 300);

	const getRepCustomerIdsData = async (
		search: string,
		loadedOptions: null,
		{ page }: { page: number },
	) => {
		const res: any = await httpService.api({
			type: 'getRepCustomers',
			data: {
				p: page,
				ps: 15,
				search,
				f: {
					unitIds: getSelectedUnits(
						filters.unitIds,
						businessUnitsList,
					),
				},
			},
			disableBI: true,
		});

		return {
			options: res?.data?.map((el: any) => {
				return {
					value: el.numericId,
					label: el.name + ` (${el.id})`,
				};
			}),
			hasMore: res?.hasNext,
			additional: {
				page: page + 1,
			},
		};
	};

	const filtersConfig = useMemo(() => {
		const statusOptions = statusOptions126(
			filters.type?.length > 1
				? 'all'
				: filters.type.includes('IRS')
				? 'IRS'
				: 'SOCIAL_SECURITY',
			true,
		);
		return compact([
			{
				key: `${filters.unitIds}${filters.type}-status`,
				name: 'status',
				options: statusOptions,
				value: filters.status.map(id =>
					statusOptions.find(item => item.id === id),
				),
				noSpecialAll: true,
				placeholder: 'status',
				alwaysRerender: true,
				styles: { width: 180, control: { maxWidth: 180 } },
				type: 'multiselect',
			},
			{
				key: `${filters.unitIds}-${filters.type}-reportType`,
				name: 'reportType',
				options: reportTypeOptions(
					filters.type?.length > 1
						? 'all'
						: filters.type.includes('IRS')
						? 'IRS'
						: 'SOCIAL_SECURITY',
				),
				placeholder:
					'repReports.repOrg126Reports.reportType.placeholder',
				value: reportTypeOptions(
					filters.type?.length > 1
						? 'all'
						: filters.type.includes('IRS')
						? 'IRS'
						: 'SOCIAL_SECURITY',
				).filter((item: any) => filters.reportType.includes(item.id)),
				noSpecialAll: true,
				forceNoAll:
					filters.type.includes('IRS') && filters.type?.length === 1
						? true
						: false,
				alwaysRerender:
					filters.type.includes('IRS') && filters.type?.length === 1
						? true
						: false,
				disabled:
					filters.type.includes('IRS') && filters.type?.length === 1
						? true
						: false,
				styles: {
					width: 180,
					control: {
						maxWidth: 180,
						opacity:
							filters.type.includes('IRS') &&
							filters.type?.length === 1
								? 0.8
								: 1,
					},
				},
				type: 'multiselect',
			},
			{
				key: `type`,
				name: 'type',
				type: 'multiselect',
				alwaysRerender: true,
				options: institutionTypeList,
				forceNoAll: true,
				value: institutionTypeList.filter((item: any) =>
					filters.type.includes(item.id),
				),
				styles: {
					width: 160,
				},
			},
			{
				key: `${JSON.stringify(filters.unitIds)}-${JSON.stringify(
					filters.repCustomerIds,
				)}-${JSON.stringify(noOperators)}-operatorIds`,
				name: 'operatorIds',
				type: 'multiselect',
				value:
					operatorsList?.length === 1
						? operatorsList
						: filters.operatorIds.map(id =>
								operatorsList.find(item => item.id === id),
						  ),
				options: operatorsList,
				forceNoAll: noOperators ? true : false,
				placeholder: 'operators',
				styles: {
					width: 200,
				},
			},
			{
				key: `${JSON.stringify(filters.unitIds)}-${JSON.stringify(
					filters.repCustomerIds,
				)}-repCustomerUnitIds`,
				name: 'repCustomerUnitIds',
				type: 'asyncmultiselect',
				placeholder: 'businessUnits.placeholder',
				loadOptions: getRepCustomerUnitsList,
				styles: { width: 220 },
			},
			{
				key: `${JSON.stringify(filters.unitIds)}-repCustomerIds`,
				name: 'repCustomerIds',
				value: repCustData,
				loadOptions: getRepCustomerIdsData,
				type: 'asyncmultiselect',
				placeholder: 'customers',
				styles: { width: 250 },
			},
			{
				key: `year`,
				name: 'year',
				type: 'datepicker',
				value: [filters.year],
				placeholder: filters.year,
				showYearPicker: true,
				showQuarterYearPicker: false,
				styles: {
					width: 140,
				},
			},
			businessUnitsList?.length > 1
				? {
						name: 'unitIds',
						type: 'select',
						placeholder: 'businessUnits',
						defaultValue: filters.unitIds?.[0],
						options: businessUnitsList,
						hideDefaultOption: true,
						styles: {
							width: 150,
						},
				  }
				: undefined,
		]);
	}, [
		userData,
		businessUnitsList,
		operatorsList,
		filters.unitIds,
		filters.repCustomerIds,
		filters.type,
		filters.year,
		filters.operatorIds,
		noOperators,
		prevReportType,
		repCustData,
	]);

	const getRequestsData = async (
		first: boolean,
		newSortBy?: string,
		neworder?: string,
		filterData?: any,
		search?: string,
	) => {
		if (!filters?.unitIds.length) {
			return;
		}
		try {
			const tempFilter = filterData || filters;
			const ps = 40;
			const res = await httpService.api<any>({
				type: 'getRepresentOrgReport',
				params: { type: 126 },
				data: {
					p: first ? 0 : nextPage,
					ps: ps,
					s: newSortBy ? newSortBy : sortBy,
					sd: neworder ? neworder : order,
					f: {
						...tempFilter,
						unitIds: getSelectedUnits(
							tempFilter?.unitIds,
							businessUnitsList,
						),
						status: tempFilter?.status?.filter(
							(stat: any) => stat !== 'all',
						),
						reportType: tempFilter?.reportType?.filter(
							(stat: any) => stat !== 'all',
						),
					},
					search: search,
				},
			});

			const data = res.data;

			if (res) {
				setDisableLoad(data?.length < ps);
				if (data.length !== 0) {
					if (first) {
						setRequestsData(data);
						setNextPage(1);
					} else {
						setRequestsData([...requestsData, ...data]);
						setNextPage(nextPage + 1);
					}
				} else {
					setRequestsData([]);
				}
				setIsLoading(false);
			}
		} catch (e) {
			setIsLoading(false);
			console.log(e);
		}
	};

	const onSort = (newSort: string, order: string) => {
		setOrder(order);
		setSortBy(newSort);
		getRequestsData(true, newSort, order, filters);
	};

	return (
		<RepOrg126Reports
			navigation={navigation}
			route={route}
			data={requestsData}
			onSort={onSort}
			onLoadMore={() =>
				disabledLoad
					? null
					: debouncedGetMoreData(false, sortBy, order, filters)
			}
			refresh={() => debouncedGetMoreData(true, sortBy, order, filters)}
			onFilter={onFilter}
			filtersConfig={filtersConfig}
			disabled={disabled}
			sortBy={sortBy}
			order={order}
			filters={filters}
			isLoading={isLoading}
			canEnterCustomer={canEnterCustomer}
		/>
	);
};

export default React.memo(RepOrg126ReportsContainer);
